import { Injectable } from '@angular/core';
import {NavigationEnd, NavigationExtras, Router, UrlTree} from '@angular/router';
import {LocationStrategy} from '@angular/common';
import {filter} from 'rxjs/operators';
import {UsageAnalyticsService} from '../usage-analytics/usage-analytics.service';
import {AppConstants} from '@shared/util/app-constants';

export interface NavigationState {
  [key: string]: any;
}

@Injectable()
export class RoutingStateService {

  private history = [];
  private navigationState;

  constructor(private router: Router,
              private usageAnalyticsService: UsageAnalyticsService,
              private locationStrategy: LocationStrategy) {}

  public loadRouting(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(({urlAfterRedirects}: NavigationEnd) => {
        this.history = [urlAfterRedirects, ...this.history];
        this.clearNavigationState();
        this.usageAnalyticsService.page();
      });
  }

  public getHistory(): string[] {
    return this.history;
  }

  public getPreviousUrl(defaultUrl: string): string {
    return this.history[1] || defaultUrl;
  }

  /**
   * It's best to use {@link navigate} instead of this method, such that the {@link NavigationState} is set just before
   * the navigation occurs.
   * @param state
   */
  public setNavigationState(state: NavigationState): void {
    this.navigationState = state != null ? {...state} : null;
  }

  public navigateByUrl<T extends NavigationState>(url: string | UrlTree, navigationState?: T, extras?: NavigationExtras): Promise<boolean> {
    this.setNavigationState(navigationState);
    return this.router.navigateByUrl(url, extras)
  }

  public navigate<T extends NavigationState>(commands: any[], navigationState?: T, extras?: NavigationExtras): Promise<boolean> {
    this.setNavigationState(navigationState);
    return this.router.navigate(commands, extras);
  }

  /**
   * Tests whether this URL stays within the URL length budget. prepareExternalUrl applies the base href the same way
   * the router does when it pushes to history, so the measured path and query are the ones the browser will hold. The
   * origin is added separately because no part of the routing layer carries it.
   */
  public doesUrlFit(urlTree: UrlTree): boolean {
    const externalUrl = this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(urlTree));
    return window.location.origin.length + externalUrl.length <= AppConstants.URL_CHAR_LIMIT;
  }

  public clearNavigationState(): void {
    this.setNavigationState(null);
  }

  public getNavigationState<T extends NavigationState>(): T | null {
    return this.navigationState != null ? {...this.navigationState} : null;
  }

}
